import { useState } from 'react';

import { companyDomainApi } from 'app/services/api';
import _ from 'lodash';

import { DomainType } from 'constants/domains';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { logger } from 'utils/logger';

import { NO_POS_DOMAINS } from './constants';

// Company domains aren't in the store on every surface that needs them, so this fetches the POS
// ones per company and degrades to an empty list on failure. The failure is logged rather than
// swallowed: an empty list is also the legitimate "no POS site configured" state, so without a log
// a 403/500 is indistinguishable from it.
//
// Cancellation drops a late RESPONSE instead of aborting the request — StrictMode's double-invoked
// effect would cancel the only in-flight fetch and leave every consumer permanently empty.
const usePosDomains = companyId => {
  const [posDomains, setPosDomains] = useState(NO_POS_DOMAINS);

  useEffectSafeStrictMode(() => {
    if (_.isEmpty(companyId)) {
      return undefined;
    }

    const lifecycle = { cancelled: false };

    companyDomainApi
      .getCompanyDomains({ companyId, type: DomainType.POS })
      .then(({ data }) => {
        if (!lifecycle.cancelled) {
          // Filtered again client-side: `type` is a server-side narrowing, and callers depend on
          // this list being POS-only for correctness, not just for size.
          setPosDomains((data ?? []).filter(({ type }) => type === DomainType.POS));
        }
      })
      .catch(error => {
        logger.error(`Could not load POS domains - ${error}`);

        if (!lifecycle.cancelled) {
          setPosDomains(NO_POS_DOMAINS);
        }
      });

    return () => {
      lifecycle.cancelled = true;
    };
  }, [companyId]);

  return posDomains;
};

export default usePosDomains;
